import {
  AdminGrantResult,
  AdminProfile,
  BillingSession,
  ClientOnboardRequest,
  ClientOnBoardResponse,
  ClientRfpRequest,
  Company,
  CompanyDuplicateResponse,
  CompanyTourStepsWithCompanyResponse,
  Contact,
  ContactPatch,
  FundingExplorerSearchResponse,
  GinApplication,
  GrantCluster,
  GrantFinancingType,
  GrantOpportunityScoreData,
  GrantResult,
  GrantsViewedHistory,
  GrantTotalAmounts,
  GrantTotalsInfo,
  Industry,
  Match,
  ParsedClientTag,
  Portfolio,
  Profile,
  Program,
  ProgramTargetUser,
  Province,
  QueryFundingExplorerProps,
  ReviewRequest,
  ReviewResult,
  ReviewVerified,
  Rfp,
  RfpDescription,
  ShortlistParams,
} from '@hellodarwin/core/lib/features/entities';
import {
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  PlanDuration,
} from '@hellodarwin/core/lib/features/entities/checkout-entities';
import {
  AcceptedProgramPreview,
  GinStepDocumentRequest,
  GinStepForm,
  GinStepTask,
} from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import { UserJourneyData } from '@hellodarwin/core/lib/features/providers/analytics-provider';
import axios, { AxiosInstance } from 'axios';
import type { CompanyPatch } from '@hellodarwin/core/lib/features/entities';
import type { CompanyInvitation } from './company-invitations-types';
import type { GrantChangelogHistoryItem } from './grant-changelog-history-types';

export type { GinStepDocumentRequest };

export default class ClientApi {
  private readonly baseURL: string;
  private readonly accessToken: string;

  constructor(baseUrl: string, accessToken: string) {
    this.baseURL = baseUrl;
    this.accessToken = accessToken;
  }

  createAxios = (contentType: string = 'application/json'): AxiosInstance => {
    return axios.create({
      baseURL: this.baseURL + '/client',
      headers: {
        'Content-Type': contentType,
        Authorization: `Bearer ${this.accessToken}`,
      },
    });
  };

  //Profile
  me = async (
    userJourneyData: UserJourneyData,
    invitationToken?: string,
  ): Promise<Profile> => {
    const response = await this.createAxios().post('/me', {
      utm: userJourneyData,
      invitation_token: invitationToken,
    });
    return response.data;
  };

  fetchProfile = async (): Promise<Profile> => {
    const response = await this.createAxios().get('/profile');
    return response.data;
  };

  checkout = async (
    returnUrl: string,
    interval: PlanDuration,
    userJourneyData: UserJourneyData,
    coupon?: string,
    freeTrial?: boolean,
    closerOwnerId?: string,
    locale?: string,
  ): Promise<CheckoutSessionResponse> => {
    const payload: CheckoutSessionRequest = {
      ...userJourneyData,
      return_url: returnUrl,
      interval: interval,
      coupon: coupon,
      free_trial: freeTrial,
      closer_owner_id: closerOwnerId,
      locale: locale,
    };
    const response = await this.createAxios().post(`/checkout`, payload);

    return response.data;
  };

  updateProfile = async (data: FormData): Promise<Profile> => {
    const response = await this.createAxios('multipart/form-data').put(
      `/profile`,
      data,
    );
    return response.data;
  };

  fetchContact = async (syncSendGrid: boolean): Promise<Contact> => {
    const response = await this.createAxios().get(`/contact`, {
      params: {
        sync_sendgrid: syncSendGrid,
      },
    });
    return response.data;
  };

  updateContactById = async (data: FormData): Promise<Contact> => {
    const response = await this.createAxios('multipart/form-data').put(
      `/users`,
      data,
    );
    return response.data;
  };
  patchContactById = async (
    contactId: string,
    patch: ContactPatch,
  ): Promise<Contact> => {
    const response = await this.createAxios().patch(
      `/users/${contactId}`,
      patch,
    );
    return response.data;
  };
  updateContact = async (data: FormData): Promise<Contact> => {
    const response = await this.createAxios('multipart/form-data').put(
      `/contact`,
      data,
    );
    return response.data;
  };
  patchContact = async (patch: ContactPatch): Promise<Contact> => {
    const response = await this.createAxios().patch(`/contact`, patch);
    return response.data;
  };

  fetchCompany = async (): Promise<Company> => {
    const response = await this.createAxios().get(`/company`);
    return response.data;
  };

  patchCompany = async (
    patch: CompanyPatch,
    completeCompanyTourStep?: boolean,
  ): Promise<Company> => {
    const params = completeCompanyTourStep
      ? { complete_company_tour_step: completeCompanyTourStep }
      : {};
    const response = await this.createAxios().patch(`/company`, patch, {
      params: params,
    });
    return response.data;
  };

  updateLogo = async (companyId: string, data: FormData): Promise<Profile> => {
    const response = await this.createAxios('multipart/form-data').put<Profile>(
      `/profile/${companyId}/picture`,
      data,
    );
    return response.data;
  };

  deleteLogo = async (companyId: string): Promise<Profile> => {
    const response = await this.createAxios().delete<Profile>(
      `/profile/${companyId}/picture`,
    );
    return response.data;
  };

  fetchMatch = async (matchId: string): Promise<Match> => {
    const response = await this.createAxios().get<Match>(
      `/matchmaking/${matchId}`,
    );
    return response.data;
  };

  fetchRaisehands = async (rfpId: string): Promise<Match[]> => {
    const response = await this.createAxios().get<Match[]>(
      `/matchmaking/raisehands/${rfpId}`,
    );
    return response.data;
  };

  fetchRefusedMatches = async (rfpId: string): Promise<Match[]> => {
    const response = await this.createAxios().get<Match[]>(
      `/matchmaking/refused/${rfpId}`,
    );
    return response.data;
  };

  fetchShortlist = async (rfpId: string): Promise<Match[]> => {
    const response = await this.createAxios().get<Match[]>(
      `/matchmaking/shortlist/${rfpId}`,
    );
    return response.data;
  };

  //rfp
  fetchRfp = async (rfpId: string): Promise<Rfp> => {
    const response = await this.createAxios().get<Rfp>(`/rfp/${rfpId}`);
    return response.data;
  };

  fetchRfpMatches = async (rfpId: string): Promise<Match[]> => {
    const response = await this.createAxios().get<Match[]>(
      `/rfp/${rfpId}/matches`,
    );
    return response.data;
  };

  fetchAllRfp = async (limit: number): Promise<Rfp[]> => {
    const response = await this.createAxios().get<Rfp[]>(`/rfp?limit=${limit}`);
    return response.data;
  };

  fetchWonRfps = async (providerId: string): Promise<Rfp[]> => {
    const response = await this.createAxios().get<Rfp[]>(
      `/rfp/won/${providerId}`,
    );
    return response.data;
  };

  createRfp = async (rfp: ClientRfpRequest): Promise<Rfp> => {
    const response = await this.createAxios().post<Rfp>(`/rfp`, rfp);

    return response.data;
  };

  fetchRfpDescription = async (rfpId: string): Promise<RfpDescription[]> => {
    const response = await this.createAxios().get<RfpDescription[]>(
      `/rfpDescription/${rfpId}`,
    );

    return response.data;
  };

  //Provider

  fetchTags = async (): Promise<{ [key: string]: ParsedClientTag }> => {
    const response = await this.createAxios().get<{
      [key: string]: ParsedClientTag;
    }>(`/tags`);

    return response.data;
  };

  fetchIndustries = async (locale: string): Promise<Industry[]> => {
    const response = await this.createAxios().get<Industry[]>(
      `/tags/industries?locale=${locale}`,
    );

    return response.data;
  };

  fetchPortfoliosById = async (providerId: string): Promise<Portfolio[]> => {
    const response = await this.createAxios().get<Portfolio[]>(
      `/providers/${providerId}/portfolios`,
    );
    return response.data;
  };

  //Matches
  shortlist = async (params: ShortlistParams): Promise<Match> => {
    const response = await this.createAxios().put(
      `/matchmaking/shortlist`,
      params,
    );

    return response.data;
  };

  shortlistMessage = async (params: ShortlistParams): Promise<Match> => {
    const response = await this.createAxios().put(
      `/matchmaking/shortlistMessage`,
      params,
    );

    return response.data;
  };

  refuseProvider = async (
    matchID: string,
    refuseReason: string,
    refuseReasonSpecified: string,
  ): Promise<Match> => {
    const response = await this.createAxios().put(
      `/matchmaking/${matchID}/refuse`,
      {
        refused_reason: refuseReason,
        refused_reason_specified: refuseReasonSpecified,
      },
    );

    return response.data;
  };

  selectAsWinner = async (matchId: string): Promise<Match> => {
    const response = await this.createAxios().put(
      '/v2/matchmaking/' + matchId + '/winner',
    );

    return response.data;
  };

  viewProvider = async (matchId: string): Promise<string> => {
    const response = await this.createAxios().put(
      'matchmaking/' + matchId + '/view',
    );

    return response.data;
  };

  fetchAdmin = async (adminId: string): Promise<AdminProfile> => {
    const response = await this.createAxios().get('/admins/' + adminId);
    return response.data;
  };

  fetchCompanyAdmin = async (): Promise<AdminProfile> => {
    const response = await this.createAxios().get('/admins/company');
    return response.data;
  };

  fetchContactById = async (contactId: string): Promise<Contact> => {
    const response = await this.createAxios().get('/contacts/' + contactId);

    return response.data;
  };

  //Reviews
  reviewProvider = async (review: ReviewRequest): Promise<ReviewResult> => {
    review.review.verified = ReviewVerified.Verified;
    const response = await this.createAxios().post('/reviews', review);

    return response.data;
  };

  createCompanyIfNotExist = async (company: Company): Promise<Company> => {
    const response = await this.createAxios().post('/reviews/company', company);

    return response.data;
  };

  createContactIfNotExist = async (contact: Contact): Promise<Contact> => {
    const response = await this.createAxios().post('/reviews/contact', contact);

    return response.data;
  };

  fetchMatchReview = async (
    providerId: string,
    rfpId: string,
  ): Promise<ReviewResult[]> => {
    const response = await this.createAxios().get(
      `/reviews/match/${providerId}/${rfpId}`,
    );

    return response.data;
  };

  fetchReviews = async (company_id: string): Promise<ReviewResult[]> => {
    const response = await this.createAxios().get(
      `/reviews/client/${company_id}`,
    );

    return response.data;
  };

  getReviewURL = async (providerId: string, rfpId: string): Promise<string> => {
    const response = await this.createAxios().get(
      `/reviews/url/${providerId}/${rfpId}`,
    );
    return response.data;
  };

  cancelRfp = async (
    rfp_id: string,
    canceled_reason: string,
    canceled_reason_specified: string,
  ): Promise<string> => {
    const response = await this.createAxios().put(`/rfp/${rfp_id}/cancel`, {
      canceled_reason,
      canceled_reason_specified,
    });

    return response.data;
  };

  askMoreProvider = async (rfpId: string, message: string): Promise<Rfp> => {
    const response = await this.createAxios().put(
      `/rfp/${rfpId}/request_more_providers_reason`,
      {
        ask_more_provider: message,
      },
    );

    return response.data;
  };

  setClientRfpProgression = async (
    rfp_id: string,
    client_progression: number,
  ): Promise<string> => {
    const response = await this.createAxios().put(
      `/rfp/${rfp_id}/client-progression`,
      {
        client_progression,
      },
    );

    return response.data;
  };

  getProgram = async (programId: string, locale: string): Promise<Program> => {
    const response = await this.createAxios().get(
      `/programs/${programId}?locale=${locale}`,
    );
    return response.data;
  };

  getPrograms = async (): Promise<Program[]> => {
    const response = await this.createAxios().get(`/programs`);
    return response.data || [];
  };

  getRoadmapPrograms = async (
    locale: string,
    programTargetUser?: ProgramTargetUser,
    excludeProgramGrantStatuses?: string[],
  ): Promise<Program[]> => {
    const response = await this.createAxios().get(`/programs/roadmap`, {
      params: {
        locale,
        program_target_user: programTargetUser || ProgramTargetUser.Admin,
        ...(excludeProgramGrantStatuses?.length
          ? {
              exclude_program_grant_status:
                excludeProgramGrantStatuses.join(','),
            }
          : {}),
      },
    });
    return response.data || [];
  };

  fetchGrant = async (
    grantId: string,
    locale: string,
    programId?: string,
    countGrantView?: boolean,
  ): Promise<GrantResult> => {
    const response = await this.createAxios().get(`/grants/${grantId}`, {
      params: {
        locale,
        ...(programId !== undefined ? { programId } : {}),
        ...(countGrantView !== undefined ? { countGrantView } : {}),
      },
    });
    return response.data;
  };

  fetchGrantChangelogHistory = async (
    grantId: string,
  ): Promise<GrantChangelogHistoryItem[]> => {
    const response = await this.createAxios().get(`/grants/${grantId}/changelog`);
    return response.data || [];
  };

  /**
   * `grantProfileId` names the applicant; omitting it uses the authenticated
   * company, which an Expert workspace never is.
   */
  fetchGrantCluster = async (
    grantId: string,
    grantProfileId?: string,
  ): Promise<GrantCluster> => {
    const response = await this.createAxios().get(
      `/grants/${grantId}/grantCluster`,
      { params: grantProfileId ? { grantProfileId } : undefined },
    );
    return response.data;
  };

  fetchGrantOpportunityScore = async (
    grantId: string,
    grantProfileId?: string,
  ): Promise<GrantOpportunityScoreData> => {
    const response = await this.createAxios().get(
      `/grants/${grantId}/opportunityScore`,
      { params: grantProfileId ? { grantProfileId } : undefined },
    );
    return response.data;
  };

  fetchAcceptedPrograms = async (
    grantId: string,
    currentProgramId: string,
    locale: string,
  ): Promise<AcceptedProgramPreview[]> => {
    const response = await this.createAxios().get(
      `/grants/applications/accepted/${grantId}?programIdToExclude=${currentProgramId}&locale=${locale}`,
    );
    return response.data;
  };

  fetchGinApplication = async (
    applicationId: string,
  ): Promise<GinApplication> => {
    const response = await this.createAxios().get(
      `/gin/application/${applicationId}`,
    );
    return response.data;
  };

  fetchCreditBalance = async (): Promise<number> => {
    const response = await this.createAxios().get<number>('/balance');

    return response.data;
  };

  fetchBillingSession = async (returnUrl: string): Promise<BillingSession> => {
    const response = await this.createAxios().get(
      `/billing/session?returnUrl=${returnUrl}`,
    );

    return response.data;
  };
  fetchCompanyUsers = async (): Promise<Contact[]> => {
    const response = await this.createAxios().get<Contact[]>(`/users`);

    return response.data;
  };

  fetchCompanyInvitations = async (): Promise<CompanyInvitation[]> => {
    const response =
      await this.createAxios().get<CompanyInvitation[]>(`/company/invitations`);

    return response.data;
  };

  revokeCompanyInvitation = async (invitationId: string): Promise<void> => {
    await this.createAxios().post(
      `/company/invitations/${invitationId}/revoke`,
    );
  };

  renewCompanyInvitation = async (invitationId: string): Promise<void> => {
    await this.createAxios().post(`/company/invitations/${invitationId}/renew`);
  };

  deleteCompanyUser = async (contactID: string): Promise<string> => {
    const response = await this.createAxios().delete<string>(
      `/users/${contactID}`,
    );

    return response.data;
  };

  deleteAccount = async (): Promise<string> => {
    const response =
      await this.createAxios().delete<string>(`/contact/account`);
    return response.data;
  };

  upsertCompanyUser = async (data: FormData): Promise<Contact> => {
    const response = await this.createAxios('multipart/form-data').put(
      `/users`,
      data,
    );
    return response.data;
  };

  sendInvitationEmail = async (email: string): Promise<Contact> => {
    const response = await this.createAxios().post(
      `/company/invitationEmail/${email}`,
    );
    return response.data;
  };

  sendRequestToJoinCompanyEmail = async (companyId: string): Promise<void> => {
    await this.createAxios().post(`/company/requestEmail/${companyId}`);
  };

  getContactByEmail = async (email: string): Promise<Contact> => {
    const response = await this.createAxios().get(`/contacts/email/${email}`);
    return response.data;
  };

  queryFundingExplorer = async ({
    locale,
    page,
    limit,
    textQuery,
    companyId,
    grantProfileId,
    filterBy,
    industry,
    subindustry,
    status,
    financingType,
    region,
    provider,
    audience,
    city,
    annualRevenue,
    companySize,
    fieldChanged,
    valueChanged,
    legalStructure,
    goalsTags,
    expenseTags,
    isOnboarding,
    npoBeneficiary,
    npoScope,
    npoStream,
    industryICNPO,
    organisationAgeRequirement,
  }: QueryFundingExplorerProps & {
    isOnboarding?: boolean;
  }): Promise<FundingExplorerSearchResponse> => {
    let url = `/grants/fundingExplorer?locale=${locale}&page=${page}&limit=${limit}&includeTotals=true`;
    const financingTypeString = financingType?.toString();
    const industryString = industry?.toString();
    const subindustryString = subindustry?.toString();
    const statusString = status?.toString();
    const regionString = region?.toString();
    const providerString = provider?.toString();
    const goalsTagsString = goalsTags?.toString();
    const expenseTagsString = expenseTags?.toString();
    const audienceString = audience?.toString();
    const cityString = city?.toString();
    const npoBeneficiaryString = npoBeneficiary?.toString();
    const npoScopeString = npoScope?.toString();
    const npoStreamString = npoStream?.toString();
    const industryICNPOString = industryICNPO?.toString();
    const organisationAgeRequirementString =
      organisationAgeRequirement?.toString();
    if (textQuery !== undefined) {
      url += `&textQuery=${textQuery}`;
    }

    if (companyId !== undefined) {
      url += `&companyId=${companyId}`;
    }

    if (grantProfileId !== undefined) {
      url += `&grantProfileId=${grantProfileId}`;
    }

    if (filterBy !== undefined) {
      url += `&filterBy=${filterBy}`;
    }

    if (industry !== undefined && industry!.length > 0) {
      url += `&industry=${industryString}`;
    }

    if (subindustry !== undefined && subindustry!.length > 0) {
      url += `&subindustry=${subindustryString}`;
    }

    if (financingType !== undefined && financingType!.length > 0) {
      url += `&financingType=${financingTypeString}`;
    }

    if (status !== undefined && status!.length > 0) {
      url += `&status=${statusString}`;
    }

    if (region !== undefined && region!.length > 0) {
      url += `&region=${regionString}`;
    }

    if (annualRevenue !== undefined && annualRevenue !== '') {
      url += `&annualRevenue=${encodeURIComponent(annualRevenue)}`;
    }

    if (companySize !== undefined && companySize !== '') {
      url += `&companySize=${encodeURIComponent(companySize)}`;
    }

    if (fieldChanged !== undefined && fieldChanged !== '') {
      url += `&fieldChanged=${fieldChanged}`;
    }

    if (valueChanged !== undefined && valueChanged !== '') {
      url += `&valueChanged=${valueChanged}`;
    }

    if (legalStructure !== undefined && legalStructure !== '') {
      url += `&legalStructure=${legalStructure}`;
    }

    if (provider !== undefined && provider!.length > 0) {
      url += `&provider=${providerString}`;
    }

    if (audience !== undefined && audience!.length > 0) {
      url += `&audience=${audienceString}`;
    }

    if (city !== undefined && city!.length > 0) {
      url += `&city=${cityString}`;
    }

    if (goalsTags !== undefined && goalsTags!.length > 0) {
      url += `&goalsTags=${goalsTagsString}`;
    }

    if (expenseTags !== undefined && expenseTags.length > 0) {
      url += `&expenseTags=${expenseTagsString}`;
    }

    if (isOnboarding !== undefined) {
      url += `&isOnboarding=${isOnboarding}`;
    }

    if (npoBeneficiary !== undefined && npoBeneficiary!.length > 0) {
      url += `&npoBeneficiary=${npoBeneficiaryString}`;
    }

    if (npoScope !== undefined && npoScope!.length > 0) {
      url += `&npoScope=${npoScopeString}`;
    }

    if (npoStream !== undefined && npoStream!.length > 0) {
      url += `&npoStream=${npoStreamString}`;
    }

    if (industryICNPO !== undefined && industryICNPO!.length > 0) {
      url += `&industryICNPO=${industryICNPOString}`;
    }

    if (organisationAgeRequirement !== undefined) {
      url += `&organisationAgeRequirement=${organisationAgeRequirementString}`;
    }

    const response =
      await this.createAxios().get<FundingExplorerSearchResponse>(url);
    return response.data;
  };

  getFundingExplorerSingleGrant = async (
    grant_id: string,
    locale: string,
  ): Promise<GrantResult> => {
    const response = await this.createAxios().get<GrantResult>(
      `/grants/fundingExplorer/single/${grant_id}?locale=${locale}`,
    );
    return response.data;
  };

  fetchGrantFinancingType = async (
    locale: string,
  ): Promise<GrantFinancingType[]> => {
    const response = await this.createAxios().get<GrantFinancingType[]>(
      `grants/financingType?locale=${locale}`,
    );
    return response.data;
  };

  fetchProvinces = async (locale: string): Promise<Province[]> => {
    const response = await this.createAxios().get<Province[]>(
      `grants/province?locale=${locale}`,
    );
    return response.data;
  };

  fetchGrantById = async (grant_id: string): Promise<AdminGrantResult> => {
    const response = await this.createAxios().get<AdminGrantResult>(
      `/grants/admin/${grant_id}`,
    );
    return response.data;
  };

  fetchFundingExplorerTotalAmount = async ({
    locale,
    page,
    limit,
    textQuery,
    companyId,
    filterBy,
    industry,
    subindustry,
    status,
    financingType,
    region,
    annualRevenue,
    companySize,
    legalStructure,
    npoBeneficiary,
    npoScope,
    npoStream,
    industryICNPO,
    organisationAgeRequirement,
    goalsTags,
    expenseTags,
    provider,
    audience,
    city,
  }: QueryFundingExplorerProps): Promise<GrantTotalsInfo> => {
    let url = `/grants/fundingExplorer/totalAmount?locale=${locale}&page=${page}&limit=${limit}`;
    const financingTypeString = financingType?.toString();
    const industryString = industry?.toString();
    const subindustryString = subindustry?.toString();
    const statusString = status?.toString();
    const regionString = region?.toString();
    const goalsTagsString = goalsTags?.toString();
    const expenseTagsString = expenseTags?.toString();
    const cityString = city?.toString();
    const npoBeneficiaryString = npoBeneficiary?.toString();
    const npoScopeString = npoScope?.toString();
    const npoStreamString = npoStream?.toString();
    const industryICNPOString = industryICNPO?.toString();
    const organisationAgeRequirementString =
      organisationAgeRequirement?.toString();
    const providerString = provider?.toString();
    const audienceString = audience?.toString();
    if (textQuery !== undefined) {
      url += `&textQuery=${textQuery}`;
    }

    if (companyId !== undefined) {
      url += `&companyId=${companyId}`;
    }

    if (filterBy !== undefined) {
      url += `&filterBy=${filterBy}`;
    }

    if (industry !== undefined && industry!.length > 0) {
      url += `&industry=${industryString}`;
    }

    if (subindustry !== undefined && subindustry!.length > 0) {
      url += `&subindustry=${subindustryString}`;
    }

    if (financingType !== undefined && financingType!.length > 0) {
      url += `&financingType=${financingTypeString}`;
    }

    if (status !== undefined && status!.length > 0) {
      url += `&status=${statusString}`;
    }

    if (region !== undefined && region!.length > 0) {
      url += `&region=${regionString}`;
    }

    if (annualRevenue !== undefined && annualRevenue !== '') {
      url += `&annualRevenue=${encodeURIComponent(annualRevenue)}`;
    }

    if (companySize !== undefined && companySize !== '') {
      url += `&companySize=${encodeURIComponent(companySize)}`;
    }

    if (legalStructure !== undefined && legalStructure !== '') {
      url += `&legalStructure=${legalStructure}`;
    }

    if (goalsTags !== undefined && goalsTags!.length > 0) {
      url += `&goalsTags=${goalsTagsString}`;
    }

    if (expenseTags !== undefined && expenseTags.length > 0) {
      url += `&expenseTags=${expenseTagsString}`;
    }

    if (industryICNPO !== undefined && industryICNPO!.length > 0) {
      url += `&industryICNPO=${industryICNPOString}`;
    }

    if (npoBeneficiary !== undefined && npoBeneficiary!.length > 0) {
      url += `&npoBeneficiary=${npoBeneficiaryString}`;
    }

    if (npoScope !== undefined && npoScope!.length > 0) {
      url += `&npoScope=${npoScopeString}`;
    }

    if (npoStream !== undefined && npoStream!.length > 0) {
      url += `&npoStream=${npoStreamString}`;
    }

    if (organisationAgeRequirement !== undefined) {
      url += `&organisationAgeRequirement=${organisationAgeRequirementString}`;
    }

    if (provider !== undefined && provider!.length > 0) {
      url += `&provider=${providerString}`;
    }

    if (audience !== undefined && audience!.length > 0) {
      url += `&audience=${audienceString}`;
    }

    if (city !== undefined && city!.length > 0) {
      url += `&city=${cityString}`;
    }

    const response = await this.createAxios().get(url);
    return response.data;
  };

  fetchGrantsTotals = async (): Promise<GrantTotalAmounts[]> => {
    const response =
      await this.createAxios().get<GrantTotalAmounts[]>(`/grants/totals`);
    return response.data;
  };

  onboard = async (
    data: ClientOnboardRequest,
  ): Promise<ClientOnBoardResponse> => {
    const response = await this.createAxios().post<ClientOnBoardResponse>(
      '/profile/onboard',
      data,
    );

    return response.data;
  };

  checkDuplicateCompany = async (
    companyName: string,
  ): Promise<CompanyDuplicateResponse[]> => {
    const response = await this.createAxios().get<CompanyDuplicateResponse[]>(
      `/companies/duplicates/${companyName}`,
    );

    return response.data;
  };

  toggleProgramUpdates = async (): Promise<Contact> => {
    const response = await this.createAxios().put<Contact>(
      `/grant-notifcation/program-updates`,
    );
    return response.data;
  };

  toggleNewsletter = async (): Promise<Contact> => {
    const response = await this.createAxios().put<Contact>(
      `/grant-notifcation/newsletter`,
    );
    return response.data;
  };

  toggleGrantDigest = async (): Promise<Contact> => {
    const response = await this.createAxios().put<Contact>(
      `/grant-notifcation/grant-digest`,
    );
    return response.data;
  };

  acceptTerms = async (): Promise<Contact> => {
    const response = await this.createAxios().put<Contact>(
      `/contact/terms-and-conditions`,
    );
    return response.data;
  };

  fetchGinStepTasks = async (): Promise<GinStepTask[]> => {
    const response = await this.createAxios().get<GinStepTask[]>(
      `/ginStep/application/tasks`,
    );
    return response.data;
  };

  fetchGinStepDocumentRequests = async (
    locale: string,
  ): Promise<GinStepDocumentRequest[]> => {
    const response = await this.createAxios().get<GinStepDocumentRequest[]>(
      '/ginStep/application/document-requests',
      { params: { locale } },
    );
    return response.data;
  };

  fetchGrantsViewedHistory = async (
    page: number,
    size: number,
    locale: string,
  ): Promise<GrantsViewedHistory> => {
    const response = await this.createAxios().get(
      `/grants/history?page=${page}&size=${size}&locale=${locale}`,
    );
    return response.data;
  };

  validateGinStep = async (
    programId: string,
    ginStepId: string,
  ): Promise<GinStepForm> => {
    const response = await this.createAxios().post(
      `/ginStep/validate/${programId}/${ginStepId}`,
    );
    return response.data;
  };

  fetchCompanyTourSteps = async (
    locale?: string,
  ): Promise<CompanyTourStepsWithCompanyResponse> => {
    const response =
      await this.createAxios().get<CompanyTourStepsWithCompanyResponse>(
        `/company/tour`,
        { params: locale ? { locale } : undefined },
      );
    return response.data;
  };
}
